// Task files (TASK-FILES). A port of Work OS's task attachments (own code, `/Users/ac/Coding/Work
// OS/app/src`: `db/schema.ts` attachments + attachment_folders, `domain/task-extras.ts`,
// `app/api/{tasks/[id]/attachments, attachments/[id], attachment-folders/[id], files/[id],
// containers/[id]/files}`, `components/comm-rail.tsx` RailAttachments, `app/api/mcp/route.ts`
// list/read/upload/rename/delete_attachment). One file service over `attachment`:
//
//   files.list          a task's, a board's or a space's live files (or a task's trash); `q`
//                       narrows by name. Every row carries its uploader (person or agent) and task.
//   files.upload        multipart `file` (or `text` + `file_name`) onto a task, into a folder, or
//                       onto one of its comments (`message_id`). The type is sniffed from the bytes;
//                       at most the company's cap (default 25 MB).
//   files.get           one file.
//   files.content       the bytes, through this authenticated proxy only (no presigned URLs: a
//                       revoked grant stops the next read): `?download=true` forces a download,
//                       `?thumb=true` a 480 px JPEG of an image or a PDF's first page; byte ranges;
//                       inline only for safe previews (images but SVG, PDF, video, audio, plain
//                       text), `nosniff`, a sandboxing CSP.
//   files.text          the text of a text-like file or a PDF (extracted), else null.
//   files.update        rename (a `/` in the name moves the file into that folder chain), move to
//                       a folder, set or clear the cover.
//   files.delete        to the trash (restorable for the company's retention, default 30 days);
//   files.restore       back (to the root if its folder is gone);
//   files.emptyTrash    purges a task's trash now (rows; a blob once nothing references it).
//   files.folders / files.createFolder / files.updateFolder / files.deleteFolder
//                       a task's folders, any depth, with live file counts per subtree; deleting a
//                       folder trashes its subtree's files to the root.
//
// The agent tools (Work OS MCP names and shapes, for the runtime prompt and the MCP server):
//   list_attachments  `GET agent/attachments?taskId=`
//   read_attachment   `GET agent/attachments/{id}?maxWidth=`: base64 content (images downscaled
//                     to `maxWidth`), 5 MB cap with a hint; plus the extracted text of a PDF.
//   upload_attachment `POST agent/attachments` `{taskId,fileName,contentBase64,mimeType?,folderPath?}`
//   rename_attachment `PATCH agent/attachments/{id}` `{newName}`
//   delete_attachment `DELETE agent/attachments/{id}`: to the trash when the seat allows it
//                     (`settings.auto_approve` lists `file_delete`), else a `task_change` approval.
//
// Who: the task's rules (05; `task#viewer` reads, `task#editor` writes; a restricted task's files
// are as restricted as the task) on `company#member`. A digital employee acts through its own
// run's token (scopes `files:read` / `files:write`, on unless the seat's `settings.file_scopes`
// narrows them) or its agent key holding the scope; only on tasks it may edit, so read-only space
// access refuses every write. A client viewer has no work access. An agent's write is a
// `tool_call` step of its running run and a task event with the agent as the actor.
import { z } from "zod";
import { Approval } from "../agent-rows";
import { Timestamp, text, Uuid } from "../primitives";
import { Attachment, AttachmentFolder } from "../work-rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** The file service's limits (Work OS: `tenants.max_upload_mb` 25, `trash_retention_days` 30). */
export const FILE_LIMITS = {
  /** The default upload cap per file (`settings.files.max_upload_mb`). */
  defaultMaxUploadMb: 25,
  /** The highest cap a company may set. */
  maxUploadMbCeiling: 100,
  /** A multipart upload request at the ceiling (the file plus its parts' headers and fields). */
  maxBodyBytes: 100 * 1024 * 1024 + 64 * 1024,
  /** An agent's base64 upload at the ceiling (4/3 of the bytes, plus the JSON around it). */
  maxAgentBodyBytes: Math.ceil((100 * 1024 * 1024 * 4) / 3) + 64 * 1024,
  /** The default retention of the trash (`settings.files.trash_retention_days`). */
  defaultTrashDays: 30,
  /** `read_attachment`'s cap after any downscale (Work OS `READ_ATTACHMENT_MAX_BYTES`). */
  readMaxBytes: 5 * 1024 * 1024,
  /** Characters `files.text` returns at most (`truncated` says when it cut). */
  textMaxChars: 200_000,
  /** Thumbnail width (Work OS: 480 px JPEG). */
  thumbWidth: 480,
  /** A folder path's depth (`folderPath`, a `/` in a name). */
  maxFolderDepth: 12,
} as const;

/** The scopes of the file tools (`{resource}:{access}`, 04). */
export const FILES_READ_SCOPE = "files:read";
export const FILES_WRITE_SCOPE = "files:write";
/** `seat.settings.auto_approve` entry that lets the seat's agent trash files without approval. */
export const FILE_DELETE_APPROVAL = "file_delete";

/** How a client may show a file inline (decided by the server from the sniffed type). */
export const FilePreview = z.enum(["image", "pdf", "text", "video", "audio", "none"]);
export type FilePreview = z.infer<typeof FilePreview>;

export const FileUploader = z.object({
  membership_id: Uuid,
  /** `digital_employee` / `personal_agent`: an agent badge. */
  kind: z.enum(["person", "digital_employee", "personal_agent"]),
  name: z.string(),
});

export const FileItem = Attachment.extend({
  uploader: FileUploader,
  preview: FilePreview,
  /** Whether `files.content?thumb=true` has a thumbnail (images, PDFs). */
  has_thumb: z.boolean(),
  /** The folder's path from the task root ("v4/sources"); "" at the root. */
  folder_path: z.string(),
  /** The task the file shows under. */
  task: z
    .object({ id: Uuid, title: z.string(), board_id: Uuid, number: z.int().nullable() })
    .nullable(),
  /** The comment it is attached to (`subject_kind = message`), else null. */
  message_id: Uuid.nullable(),
  /** When a trashed file is purged; null while it is live. */
  purge_at: Timestamp.nullable(),
}).meta({ id: "FileItem" });
export type FileItem = z.infer<typeof FileItem>;

const flag = z.enum(["true", "false"]);

export const FileListQuery = PageQuery.extend({
  task_id: Uuid.optional(),
  board_id: Uuid.optional(),
  space_id: Uuid.optional(),
  /** A name search (case-insensitive substring). */
  q: z.string().trim().min(1).max(200).optional(),
  /** `true`: the task's trash instead of its live files (needs `task_id`). */
  trashed: flag.optional(),
}).meta({ id: "FileListQuery" });

export const FileUpload = z
  .object({
    file: z
      .string()
      .describe("The file (multipart part `file`); or `text` instead")
      .meta({ format: "binary" })
      .optional(),
    text: z
      .string()
      .max(FILE_LIMITS.maxUploadMbCeiling * 1024 * 1024)
      .describe("A text file's content, instead of `file` (needs `file_name`)")
      .optional(),
    file_name: text(255).optional(),
    task_id: Uuid.optional(),
    /** A comment of a task's thread to attach the file to (the task is the thread's). */
    message_id: Uuid.optional(),
    folder_id: Uuid.optional(),
    /** A `/` folder chain, made as needed (case-insensitive match), instead of `folder_id`. */
    folder_path: z.string().max(512).optional(),
  })
  .meta({ id: "FileUpload" });

export const FilePatch = z
  .strictObject({
    /** A new name; a `/` in it moves the file into that folder chain (Work OS). */
    file_name: text(255).optional(),
    /** The folder to move to (null = the root). */
    folder_id: Uuid.nullable().optional(),
    is_cover: z.boolean().optional(),
  })
  .meta({ id: "FilePatch" });

export const FileContentQuery = z.object({
  /** `true`: always `Content-Disposition: attachment`. */
  download: flag.optional(),
  /** `true`: a 480 px JPEG thumbnail (images, a PDF's first page); 404 when there is none. */
  thumb: flag.optional(),
  /**
   * An image's small copy, WebP with no metadata: `thumb` (400 px long edge) or `preview` (1600
   * px), served with a long immutable cache. Without it (or when the file has no copy) the
   * original is served, which is what download, "open original" and agent tools get.
   */
  size: z.enum(["thumb", "preview"]).optional(),
});

export const FileText = z
  .object({
    file: FileItem,
    /** The text (a text-like file, or a PDF's extracted text); null when there is none. */
    text: z.string().nullable(),
    truncated: z.boolean(),
  })
  .meta({ id: "FileText" });
export type FileText = z.infer<typeof FileText>;

export const FilesEmptied = z.object({ purged: z.int().min(0) }).meta({ id: "FilesEmptied" });

export const FileFolderItem = AttachmentFolder.extend({
  /** Live files in the folder and every folder under it (Work OS `foldersWithLiveCounts`). */
  live_files: z.int().min(0),
}).meta({ id: "FileFolderItem" });

export const FileFolderCreate = z
  .strictObject({ name: text(120), parent_id: Uuid.nullable().optional() })
  .meta({ id: "FileFolderCreate" });

export const FileFolderPatch = z
  .strictObject({ name: text(120).optional(), parent_id: Uuid.nullable().optional() })
  .meta({ id: "FileFolderPatch" });

// Agent tools (Work OS MCP shapes) ----------------------------------------------------------

export const AgentAttachment = z
  .object({
    id: Uuid,
    fileName: z.string(),
    /** The folder path ("" = the task root). */
    folder: z.string(),
    mimeType: z.string(),
    sizeBytes: z.int(),
    uploadedByType: z.enum(["human", "agent", "system"]),
    createdAt: Timestamp,
  })
  .meta({ id: "AgentAttachment" });

export const AgentAttachmentsQuery = z.object({ taskId: Uuid });

export const AgentAttachmentReadQuery = z.object({
  /** Downscale an image to this width (px) before returning it. */
  maxWidth: z.coerce.number().int().min(16).max(4096).optional(),
});

export const AgentAttachmentContent = z
  .object({
    id: Uuid,
    fileName: z.string(),
    /** The returned bytes' type (`image/jpeg` after a downscale). */
    mimeType: z.string(),
    sizeBytes: z.int(),
    contentBase64: z.string(),
    /** `image`: the MCP server returns it as image content. */
    kind: z.enum(["image", "file"]),
    /** TASK-FILES: a PDF's or text file's text, when it has one (null otherwise). */
    text: z.string().nullable(),
  })
  .meta({ id: "AgentAttachmentContent" });

export const AgentAttachmentUpload = z
  .strictObject({
    taskId: Uuid,
    fileName: text(255),
    contentBase64: z.string().min(1).max(FILE_LIMITS.maxAgentBodyBytes),
    mimeType: z.string().max(255).optional(),
    folderPath: z.string().max(512).optional(),
  })
  .meta({ id: "AgentAttachmentUpload" });

export const AgentAttachmentUploaded = z
  .object({ id: Uuid, fileName: z.string(), sizeBytes: z.int(), folder: z.string() })
  .meta({ id: "AgentAttachmentUploaded" });

export const AgentAttachmentRename = z
  .strictObject({ newName: text(512) })
  .meta({ id: "AgentAttachmentRename" });

export const AgentAttachmentRenamed = z
  .object({ id: Uuid, fileName: z.string(), folder: z.string() })
  .meta({ id: "AgentAttachmentRenamed" });

export const AgentAttachmentDeleted = z
  .object({
    ok: z.literal(true),
    /** Trashed now; false when the delete waits for a person's approval. */
    deleted: z.boolean(),
    approval: Approval.nullable(),
  })
  .meta({ id: "AgentAttachmentDeleted" });

export const fileRoutes = {
  "files.list": defineRoute({
    ...core,
    id: "files.list",
    method: "get",
    path: `${base}/files`,
    tag: "files",
    summary: "Files of a task, a board or a space (or a task's trash) that the caller may see",
    access: "member",
    params: CompanyParams,
    query: FileListQuery,
    response: { status: 200, schema: page(FileItem) },
    errors: ["invalid_cursor", "scope_missing"],
    emits: [],
  }),
  "files.upload": defineRoute({
    ...core,
    id: "files.upload",
    method: "post",
    path: `${base}/files`,
    tag: "files",
    summary: "Upload a file (or make one from text) onto a task, a folder or one of its comments",
    access: "member",
    params: CompanyParams,
    body: FileUpload,
    bodyType: "multipart",
    idempotency: "optional",
    response: { status: 201, schema: FileItem },
    errors: ["payload_too_large", "scope_missing"],
    emits: ["task.file_changed", "task.file_folder_changed", "run.step_recorded"],
  }),
  "files.get": defineRoute({
    ...core,
    id: "files.get",
    method: "get",
    path: `${base}/files/{id}`,
    tag: "files",
    summary: "One file (the task's rules decide who may see it)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: FileItem },
    errors: ["scope_missing"],
    emits: [],
  }),
  "files.content": defineRoute({
    ...core,
    id: "files.content",
    method: "get",
    path: `${base}/files/{id}/content`,
    tag: "files",
    summary: "A file's bytes or thumbnail through the authenticated proxy (byte ranges)",
    access: "member",
    params: CompanyItemParams,
    query: FileContentQuery,
    response: { status: 200, contentType: "application/octet-stream" },
    errors: ["scope_missing"],
    emits: [],
  }),
  "files.text": defineRoute({
    ...core,
    id: "files.text",
    method: "get",
    path: `${base}/files/{id}/text`,
    tag: "files",
    summary: "A file's text (text files; PDFs extracted)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: FileText },
    errors: ["scope_missing"],
    emits: [],
  }),
  "files.update": defineRoute({
    ...core,
    id: "files.update",
    method: "patch",
    path: `${base}/files/{id}`,
    tag: "files",
    summary: "Rename a file, move it to a folder, or set or clear it as a cover",
    access: "member",
    params: CompanyItemParams,
    body: FilePatch,
    response: { status: 200, schema: FileItem },
    errors: ["scope_missing"],
    emits: ["task.file_changed", "task.file_folder_changed", "run.step_recorded"],
  }),
  "files.delete": defineRoute({
    ...core,
    id: "files.delete",
    method: "delete",
    path: `${base}/files/{id}`,
    tag: "files",
    summary: "Move a file to the trash (restorable for the company's retention)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: FileItem },
    errors: ["scope_missing"],
    emits: ["task.file_changed", "run.step_recorded"],
  }),
  "files.restore": defineRoute({
    ...core,
    id: "files.restore",
    method: "post",
    path: `${base}/files/{id}/restore`,
    tag: "files",
    summary: "Restore a file from the trash (to the root if its folder is gone)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: FileItem },
    errors: ["scope_missing"],
    emits: ["task.file_changed", "run.step_recorded"],
  }),
  "files.emptyTrash": defineRoute({
    ...core,
    id: "files.emptyTrash",
    method: "delete",
    path: `${base}/tasks/{id}/files/trash`,
    tag: "files",
    summary: "Purge a task's trashed files now (a blob once nothing references it)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: FilesEmptied },
    errors: ["scope_missing"],
    emits: ["task.trash_emptied"],
  }),
  "files.folders": defineRoute({
    ...core,
    id: "files.folders",
    method: "get",
    path: `${base}/tasks/{id}/file-folders`,
    tag: "files",
    summary: "A task's file folders with live file counts per subtree",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(FileFolderItem) }) },
    errors: ["scope_missing"],
    emits: [],
  }),
  "files.createFolder": defineRoute({
    ...core,
    id: "files.createFolder",
    method: "post",
    path: `${base}/tasks/{id}/file-folders`,
    tag: "files",
    summary: "Add a folder to a task's files (under a parent folder or at the root)",
    access: "member",
    params: CompanyItemParams,
    body: FileFolderCreate,
    response: { status: 201, schema: FileFolderItem },
    errors: ["scope_missing"],
    emits: ["task.file_folder_changed"],
  }),
  "files.updateFolder": defineRoute({
    ...core,
    id: "files.updateFolder",
    method: "patch",
    path: `${base}/file-folders/{id}`,
    tag: "files",
    summary: "Rename a folder or move it under another (never under itself)",
    access: "member",
    params: CompanyItemParams,
    body: FileFolderPatch,
    response: { status: 200, schema: FileFolderItem },
    errors: ["scope_missing"],
    emits: ["task.file_folder_changed"],
  }),
  "files.deleteFolder": defineRoute({
    ...core,
    id: "files.deleteFolder",
    method: "delete",
    path: `${base}/file-folders/{id}`,
    tag: "files",
    summary: "Remove a folder and its subtree; their files go to the trash (restored to the root)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: ["scope_missing"],
    emits: ["task.file_folder_changed", "task.file_changed"],
  }),

  // The agent tools (Work OS MCP names in the summaries).
  "agentFiles.list": defineRoute({
    ...core,
    id: "agentFiles.list",
    method: "get",
    path: `${base}/agent/attachments`,
    tag: "agent-files",
    summary: "list_attachments: a task's live files (files:read)",
    access: "run_token",
    params: CompanyParams,
    query: AgentAttachmentsQuery,
    response: { status: 200, schema: z.object({ data: z.array(AgentAttachment) }) },
    errors: ["scope_missing"],
    emits: [],
  }),
  "agentFiles.read": defineRoute({
    ...core,
    id: "agentFiles.read",
    method: "get",
    path: `${base}/agent/attachments/{id}`,
    tag: "agent-files",
    summary:
      "read_attachment: a file's content as base64 (images downscaled; 5 MB cap) (files:read)",
    access: "run_token",
    params: CompanyItemParams,
    query: AgentAttachmentReadQuery,
    response: { status: 200, schema: AgentAttachmentContent },
    errors: ["scope_missing", "payload_too_large"],
    emits: [],
  }),
  "agentFiles.upload": defineRoute({
    ...core,
    id: "agentFiles.upload",
    method: "post",
    path: `${base}/agent/attachments`,
    tag: "agent-files",
    summary:
      "upload_attachment: a base64 file onto a task; `folderPath` makes nested folders (files:write)",
    access: "run_token",
    params: CompanyParams,
    body: AgentAttachmentUpload,
    idempotency: "optional",
    response: { status: 201, schema: AgentAttachmentUploaded },
    errors: ["scope_missing", "payload_too_large"],
    emits: ["task.file_changed", "task.file_folder_changed", "run.step_recorded"],
  }),
  "agentFiles.rename": defineRoute({
    ...core,
    id: "agentFiles.rename",
    method: "patch",
    path: `${base}/agent/attachments/{id}`,
    tag: "agent-files",
    summary:
      "rename_attachment: a new name; slashes move the file into that folder chain (files:write)",
    access: "run_token",
    params: CompanyItemParams,
    body: AgentAttachmentRename,
    response: { status: 200, schema: AgentAttachmentRenamed },
    errors: ["scope_missing"],
    emits: ["task.file_changed", "task.file_folder_changed", "run.step_recorded"],
  }),
  "agentFiles.delete": defineRoute({
    ...core,
    id: "agentFiles.delete",
    method: "delete",
    path: `${base}/agent/attachments/{id}`,
    tag: "agent-files",
    summary:
      "delete_attachment: to the trash, or a `task_change` approval unless the seat auto-approves `file_delete` (files:write)",
    access: "run_token",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentAttachmentDeleted },
    errors: ["scope_missing", "supervisor_required"],
    emits: ["task.file_changed", "approval.requested", "run.step_recorded"],
  }),
} as const;
